"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState } from "react"

export function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const pathname = usePathname()
  // Homepage uses transparent-until-scrolled. Every other route is always
  // opaque so the header doesn't sit invisibly over legal/pricing copy.
  const variant: 'default' | 'pricing' = pathname === '/' ? 'default' : 'pricing'

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!menuOpen) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    const onPointerDown = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [menuOpen])

  const navItems = [
    { label: 'Capabilities', href: '/#capabilities' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'FAQ', href: '/#faq' },
  ]

  // Only highlight true page-level routes. Anchor links (/#capabilities, /#faq)
  // are sections of the homepage, not separate pages, so they never get
  // a persistent active state.
  const isActive = (href: string) => {
    if (href.startsWith('/#')) return false
    return pathname === href
  }

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || variant === 'pricing'
          ? 'bg-[#F7F7F4]/85 backdrop-blur-md border-b border-black/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <span className="font-display text-lg tracking-tight font-semibold text-[#0A0A0A]">
            Ziannah
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {navItems.map((item) => {
            const active = isActive(item.href)
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                className={`px-3 py-2 text-sm transition-colors rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-black/30 ${
                  active
                    ? 'text-[#0A0A0A] font-medium'
                    : 'text-[#0A0A0A]/75 hover:text-[#0A0A0A]'
                }`}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="mailto:hello@ziannah.com"
            className="hidden sm:inline-flex items-center px-3 h-9 rounded-full text-sm text-[#0A0A0A]/80 hover:text-[#0A0A0A] hover:bg-black/5 transition-colors"
          >
            Contact
          </a>
          <Link
            href="/#waitlist"
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-black transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black/50 focus-visible:ring-offset-[#F7F7F4]"
          >
            Get early access
          </Link>

          <button
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen(v => !v)}
            className="md:hidden p-2 -mr-2 text-[#0A0A0A]"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {menuOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div id="mobile-menu" className="md:hidden border-t border-black/5 bg-[#F7F7F4]/95 backdrop-blur-md">
          <div className="max-w-[1440px] mx-auto px-4 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="px-3 py-3 text-base text-[#0A0A0A]/85 hover:text-[#0A0A0A] rounded-md"
              >
                {item.label}
              </Link>
            ))}
            <Link
              href="/#waitlist"
              onClick={() => setMenuOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 px-4 h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium"
            >
              Get early access
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}
